import React, { useState, useEffect } from 'react';
import { getHours, reviewHours, bulkReviewHours } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, Clock, MessageSquare, CheckCheck, XOctagon } from 'lucide-react';
import { toast } from 'sonner';

export default function ApproveHoursPage() {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [notes, setNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  useEffect(() => { loadHours(); }, []);

  const loadHours = async () => {
    setLoading(true);
    try {
      const { data } = await getHours({ status: 'pending' });
      setHours(data.hours || []);
      setSelectedIds(new Set());
    } catch (e) { console.error('Failed to load hours', e); }
    finally { setLoading(false); }
  };

  const handleReview = async () => {
    if (!selectedLog) return;
    setProcessing(true);
    try {
      await reviewHours(selectedLog.log_id, { status: reviewAction, notes });
      toast.success(`Hours ${reviewAction} successfully`);
      setSelectedLog(null);
      setNotes('');
      loadHours();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to review hours'); }
    finally { setProcessing(false); }
  };

  const openReview = (log, action) => {
    setSelectedLog(log);
    setReviewAction(action);
    setNotes('');
  };

  const toggleSelect = (logId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(logId)) next.delete(logId);
      else next.add(logId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === hours.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(hours.map(h => h.log_id)));
    }
  };

  const handleBulkReview = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setBulkProcessing(true);
    try {
      const ids = bulkAction === 'approve_all' || bulkAction === 'reject_all'
        ? hours.map(h => h.log_id)
        : Array.from(selectedIds);
      const status = bulkAction.includes('approve') ? 'approved' : 'rejected';
      const result = await bulkReviewHours({ log_ids: ids, status, notes: bulkNotes });
      toast.success(`${result.data.processed} entries ${status}. ${result.data.emails_queued} emails queued.`);
      setBulkAction(null);
      setBulkNotes('');
      loadHours();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk review failed'); }
    finally { setBulkProcessing(false); }
  };

  const openBulk = (action) => {
    setBulkAction(action);
    setBulkNotes('');
  };

  const bulkCount = bulkAction === 'approve_all' || bulkAction === 'reject_all' ? hours.length : selectedIds.size;

  return (
    <div className="animate-fade-in" data-testid="approve-hours-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">Approve Hours</h1>
          <p className="text-[#5C5855] mt-1">Review and approve pending volunteer hours</p>
        </div>
        {hours.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedIds.size > 0 && (
              <>
                <Button size="sm" onClick={() => openBulk('approve_selected')}
                  className="bg-[#2C5545] hover:bg-[#1a3a2e] text-white text-xs" data-testid="approve-selected-btn">
                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve Selected ({selectedIds.size})
                </Button>
                <Button size="sm" variant="outline" onClick={() => openBulk('reject_selected')}
                  className="border-[#B74134] text-[#B74134] hover:bg-[#B74134]/10 text-xs" data-testid="reject-selected-btn">
                  <XCircle className="h-3.5 w-3.5 mr-1" /> Reject Selected ({selectedIds.size})
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => openBulk('approve_all')}
              className="bg-[#3F6352] hover:bg-[#2C4A3C] text-white text-xs" data-testid="approve-all-btn">
              <CheckCheck className="h-3.5 w-3.5 mr-1" /> Approve All ({hours.length})
            </Button>
            <Button size="sm" variant="outline" onClick={() => openBulk('reject_all')}
              className="border-[#B74134] text-[#B74134] hover:bg-[#B74134]/10 text-xs" data-testid="reject-all-btn">
              <XOctagon className="h-3.5 w-3.5 mr-1" /> Reject All ({hours.length})
            </Button>
          </div>
        )}
      </div>

      <div className="w-full rounded-xl border border-[#E8E3DD] overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FAF8F5] border-b border-[#E8E3DD]">
              <TableHead className="w-12 px-4 py-4">
                {hours.length > 0 && (
                  <Checkbox
                    checked={selectedIds.size === hours.length && hours.length > 0}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                )}
              </TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Volunteer</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Date</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Service Type</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Hours</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Description</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i} className="border-b border-[#E8E3DD]">
                  <TableCell colSpan={7} className="px-6 py-4"><div className="h-4 bg-[#F2EFEB] rounded animate-pulse" /></TableCell>
                </TableRow>
              ))
            ) : hours.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-[#8A847F]">
                  <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No pending hours to review</p>
                </TableCell>
              </TableRow>
            ) : (
              hours.map((log) => (
                <TableRow key={log.log_id} className={`border-b border-[#E8E3DD] hover:bg-[#F2EFEB]/50 transition-colors ${selectedIds.has(log.log_id) ? 'bg-[#D3735A]/5' : ''}`} data-testid={`approve-row-${log.log_id}`}>
                  <TableCell className="w-12 px-4 py-4">
                    <Checkbox
                      checked={selectedIds.has(log.log_id)}
                      onCheckedChange={() => toggleSelect(log.log_id)}
                      data-testid={`select-${log.log_id}`}
                    />
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <div>
                      <p className="text-sm font-semibold text-[#2D2B2A]">{log.volunteer_name}</p>
                      <p className="text-xs text-[#8A847F] font-mono">{log.volunteer_id}</p>
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.date}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#2D2B2A]">{log.service_type}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#2D2B2A] font-semibold">{log.hours}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#5C5855] max-w-[180px] truncate">{log.description || '—'}</TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button data-testid={`approve-btn-${log.log_id}`} size="sm"
                        onClick={() => openReview(log, 'approved')}
                        className="bg-[#2C5545] hover:bg-[#1a3a2e] text-white text-xs px-3 py-1.5 h-auto">
                        <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                      <Button data-testid={`reject-btn-${log.log_id}`} size="sm"
                        onClick={() => openReview(log, 'rejected')}
                        variant="outline" className="border-[#B74134] text-[#B74134] hover:bg-[#B74134]/10 text-xs px-3 py-1.5 h-auto">
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Single Review Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="bg-white" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">
              {reviewAction === 'approved' ? 'Approve' : 'Reject'} Hours
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="bg-[#FAF8F5] rounded-lg p-4 space-y-2">
                <p className="text-sm"><strong>Volunteer:</strong> {selectedLog.volunteer_name}</p>
                <p className="text-sm"><strong>Date:</strong> {selectedLog.date}</p>
                <p className="text-sm"><strong>Service:</strong> {selectedLog.service_type}</p>
                <p className="text-sm"><strong>Hours:</strong> {selectedLog.hours}</p>
                {selectedLog.description && <p className="text-sm"><strong>Description:</strong> {selectedLog.description}</p>}
              </div>
              <div>
                <Label className="block text-sm font-semibold text-[#5C5855] mb-2">
                  <MessageSquare className="h-4 w-4 inline mr-1" /> Notes (optional)
                </Label>
                <Textarea data-testid="review-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add a note..." rows={3} className="border-[#D1C9C0]" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedLog(null)} className="border-[#D1C9C0]" data-testid="review-cancel">Cancel</Button>
            <Button data-testid="review-confirm" onClick={handleReview} disabled={processing}
              className={reviewAction === 'approved' ? 'bg-[#2C5545] hover:bg-[#1a3a2e] text-white' : 'bg-[#B74134] hover:bg-[#9a3229] text-white'}>
              {processing ? 'Processing...' : reviewAction === 'approved' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Review Dialog */}
      <Dialog open={!!bulkAction} onOpenChange={(open) => !open && setBulkAction(null)}>
        <DialogContent className="bg-white" data-testid="bulk-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">
              {bulkAction?.includes('approve') ? 'Bulk Approve' : 'Bulk Reject'} Hours
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#FAF8F5] rounded-lg p-4">
              <p className="text-sm text-[#2D2B2A]">
                You are about to <strong>{bulkAction?.includes('approve') ? 'approve' : 'reject'}</strong>{' '}
                <strong>{bulkCount}</strong> pending hour {bulkCount === 1 ? 'entry' : 'entries'}.
                Email notifications will be sent to each volunteer.
              </p>
            </div>
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">
                <MessageSquare className="h-4 w-4 inline mr-1" /> Notes for all (optional)
              </Label>
              <Textarea data-testid="bulk-review-notes" value={bulkNotes} onChange={(e) => setBulkNotes(e.target.value)}
                placeholder="This note will be applied to all entries..." rows={3} className="border-[#D1C9C0]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAction(null)} className="border-[#D1C9C0]" data-testid="bulk-cancel">Cancel</Button>
            <Button data-testid="bulk-confirm" onClick={handleBulkReview} disabled={bulkProcessing}
              className={bulkAction?.includes('approve') ? 'bg-[#2C5545] hover:bg-[#1a3a2e] text-white' : 'bg-[#B74134] hover:bg-[#9a3229] text-white'}>
              {bulkProcessing ? 'Processing...' : `${bulkAction?.includes('approve') ? 'Approve' : 'Reject'} ${bulkCount} entries`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
